import { ref, computed, watch, type Ref } from 'vue';
import { aiService } from '@/services/AIService';
import type { FieldOverride, CreateFieldOverrideRequest } from '@/types/ai';

export function useFieldOverrides(promptResultId: Ref<number | null>) {
  // State
  const overrides = ref<Map<string, FieldOverride>>(new Map());
  const pendingLocalChanges = ref<Map<string, string | number | boolean>>(new Map());
  const pendingSaves = ref<Set<string>>(new Set());
  const saveErrors = ref<Map<string, string>>(new Map());
  const isLoading = ref(false);
  const isReviewMode = ref(false);
  const isSavingAll = ref(false);

  // Computed
  const overrideCount = computed(() => overrides.value.size);
  const overriddenFieldPaths = computed(() => Array.from(overrides.value.keys()));
  const hasPendingChanges = computed(() => pendingLocalChanges.value.size > 0);
  const pendingChangeCount = computed(() => pendingLocalChanges.value.size);

  // Watch for promptResultId changes to reset state
  watch(promptResultId, () => {
    overrides.value.clear();
    pendingLocalChanges.value.clear();
    pendingSaves.value.clear();
    saveErrors.value.clear();
    isReviewMode.value = false;
  });

  // Methods
  const loadOverrides = async (): Promise<void> => {
    if (!promptResultId.value) return;

    isLoading.value = true;
    try {
      const response = await aiService.getFieldOverrides(promptResultId.value);
      overrides.value.clear();
      response.overrides.forEach((override) => {
        overrides.value.set(override.fieldPath, override);
      });
    } catch (error) {
      console.error('Failed to load overrides:', error);
    } finally {
      isLoading.value = false;
    }
  };

  const saveOverride = async (fieldPath: string, value: string | number | boolean): Promise<boolean> => {
    if (!promptResultId.value) return false;

    pendingSaves.value.add(fieldPath);
    saveErrors.value.delete(fieldPath);

    try {
      const request: CreateFieldOverrideRequest = {
        fieldPath,
        overrideValue: value,
      };
      await aiService.createFieldOverride(promptResultId.value, request);

      // Reload overrides to get the updated override with server-generated fields
      await loadOverrides();
      return true;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save';
      saveErrors.value.set(fieldPath, errorMessage);
      return false;
    } finally {
      pendingSaves.value.delete(fieldPath);
    }
  };

  const revertOverride = async (fieldPath: string): Promise<boolean> => {
    if (!promptResultId.value) return false;

    pendingSaves.value.add(fieldPath);
    saveErrors.value.delete(fieldPath);

    try {
      await aiService.deleteFieldOverride(promptResultId.value, fieldPath);
      overrides.value.delete(fieldPath);
      return true;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to revert';
      saveErrors.value.set(fieldPath, errorMessage);
      return false;
    } finally {
      pendingSaves.value.delete(fieldPath);
    }
  };

  const getOverride = (fieldPath: string): FieldOverride | undefined => {
    return overrides.value.get(fieldPath);
  };

  const hasOverride = (fieldPath: string): boolean => {
    return overrides.value.has(fieldPath);
  };

  const isPending = (fieldPath: string): boolean => {
    return pendingSaves.value.has(fieldPath);
  };

  const getError = (fieldPath: string): string | undefined => {
    return saveErrors.value.get(fieldPath);
  };

  const clearError = (fieldPath: string): void => {
    saveErrors.value.delete(fieldPath);
  };

  // Local change tracking (deferred save)
  const setLocalOverride = (fieldPath: string, value: string | number | boolean): void => {
    pendingLocalChanges.value.set(fieldPath, value);
    saveErrors.value.delete(fieldPath);
  };

  const revertLocalOverride = (fieldPath: string): void => {
    pendingLocalChanges.value.delete(fieldPath);
    saveErrors.value.delete(fieldPath);
  };

  const hasLocalChange = (fieldPath: string): boolean => {
    return pendingLocalChanges.value.has(fieldPath);
  };

  const getLocalChange = (fieldPath: string): string | number | boolean | undefined => {
    return pendingLocalChanges.value.get(fieldPath);
  };

  const clearPendingChanges = (): void => {
    pendingLocalChanges.value.clear();
  };

  const saveAllPendingOverrides = async (): Promise<boolean> => {
    if (!promptResultId.value || pendingLocalChanges.value.size === 0) return true;

    isSavingAll.value = true;
    const failedPaths: string[] = [];

    try {
      const entries = Array.from(pendingLocalChanges.value.entries());

      for (const [fieldPath, value] of entries) {
        pendingSaves.value.add(fieldPath);
        try {
          const existingOverride = overrides.value.get(fieldPath);

          // If value matches original and there's an existing override, delete it
          if (existingOverride && existingOverride.originalValue === value) {
            await aiService.deleteFieldOverride(promptResultId.value, fieldPath);
            overrides.value.delete(fieldPath);
          } else {
            // Otherwise, create/update the override
            const request: CreateFieldOverrideRequest = {
              fieldPath,
              overrideValue: value,
            };
            await aiService.createFieldOverride(promptResultId.value, request);
          }
          pendingLocalChanges.value.delete(fieldPath);
        } catch (error: unknown) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to save';
          saveErrors.value.set(fieldPath, errorMessage);
          failedPaths.push(fieldPath);
        } finally {
          pendingSaves.value.delete(fieldPath);
        }
      }

      // Reload overrides to get server-generated fields
      if (failedPaths.length < entries.length) {
        await loadOverrides();
      }

      return failedPaths.length === 0;
    } finally {
      isSavingAll.value = false;
    }
  };

  const enterReviewMode = async (): Promise<void> => {
    await loadOverrides();
    isReviewMode.value = true;
  };

  const exitReviewMode = (): void => {
    isReviewMode.value = false;
  };

  const toggleReviewMode = async (): Promise<void> => {
    if (isReviewMode.value) {
      exitReviewMode();
    } else {
      await enterReviewMode();
    }
  };

  return {
    // State
    overrides,
    isLoading,
    isReviewMode,
    isSavingAll,
    overrideCount,
    overriddenFieldPaths,
    hasPendingChanges,
    pendingChangeCount,

    // Methods
    loadOverrides,
    saveOverride,
    revertOverride,
    getOverride,
    hasOverride,
    isPending,
    getError,
    clearError,
    enterReviewMode,
    exitReviewMode,
    toggleReviewMode,

    // Local change tracking (deferred save)
    setLocalOverride,
    revertLocalOverride,
    hasLocalChange,
    getLocalChange,
    clearPendingChanges,
    saveAllPendingOverrides,
  };
}
